import { canStartOrSubmitFlow, parseDbTimestamp } from "@repo/shared";
import { clientTypeAllowedForFlow, isFlowInstanceEditable } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { FlowStageBadge } from "@/components/flow/flow-stage-badge";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  rowControl,
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { del, post } from "@/lib/api/_client";
import { useAppBootstrap } from "@/lib/hooks/use-app-bootstrap";
import { useClient } from "@/lib/hooks/use-client-detail";
import { useFlows } from "@/lib/hooks/use-flows";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { cn, getRelativeTime } from "@/lib/utils";
import { T } from "./client-flows-section.i18n";

type SignatureOrder = {
  id: string;
  status: string;
  signatureMethod: string;
  signedAt: string | null;
};

type FlowInstance = {
  id: string;
  name: string | null;
  status: string;
  startedAt: string;
  completedAt: string | null;
  updatedAt: string;
  externalStatus: string | null;
  externalStatusInfo: string | null;
  flow: {
    name: string;
  };
  signatureOrders: SignatureOrder[];
};

type ClientFlowsSectionProps = {
  clientId: string;
  instances: FlowInstance[];
  onMutate: () => void;
  autoOpenDialog?: boolean;
  onAutoOpenDialogConsumed?: () => void;
};

export function ClientFlowsSection({
  clientId,
  instances,
  onMutate,
  autoOpenDialog,
  onAutoOpenDialogConsumed,
}: ClientFlowsSectionProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const { flows } = useFlows();
  // QP-184 — hide flows gated to other client types (server enforces the same
  // rule on create; null/empty allowedClientTypes = open to all).
  const { client } = useClient(clientId);
  const clientType: string | undefined = client?.type;
  // QL-239 — the same rule the server enforces on create: active flows for
  // everyone, preview flows for designers only.
  const bootstrap = useAppBootstrap().data;
  const permissions = bootstrap?.user.permissions ?? [];
  const startableFlows = flows.filter(
    (f) =>
      canStartOrSubmitFlow(f.status, "app", permissions, "start") &&
      clientTypeAllowedForFlow(f.allowedClientTypes, clientType),
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [_isCreating, setIsCreating] = useState(false);

  // Wait for the permissions: before they load, a designer's preview flows are
  // missing from the list and the dialog would open without them (or not at all).
  const permissionsLoaded = !!bootstrap;
  useEffect(() => {
    if (autoOpenDialog && permissionsLoaded && startableFlows.length > 0 && !dialogOpen) {
      setDialogOpen(true);
      onAutoOpenDialogConsumed?.();
    }
  }, [
    autoOpenDialog,
    permissionsLoaded,
    startableFlows.length,
    dialogOpen,
    onAutoOpenDialogConsumed,
  ]);

  // Dialog states
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingInstanceId, setDeletingInstanceId] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleStartFlow = async (flowId: string) => {
    setIsCreating(true);
    try {
      const data = await post<{ id: string }>("/internal/flow-instances", { flowId, clientId });
      // Remove cached flow list so it refetches on next mount.
      // Using removeQueries instead of invalidateQueries to avoid an
      // immediate refetch while the list component is still mounted.
      queryClient.removeQueries({ queryKey: ["clients", clientId, "flowInstances"] });
      // QL-275 — a new instance appears as "Created" in the Activity tab.
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, "activityEvents"] });
      navigate(`/clients/${clientId}/flows/${data.id}`);
    } catch (err: any) {
      showAlert(t.error, err?.message || t.failedToStartFlow, "destructive");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (instanceId: string) => {
    setDeletingInstanceId(instanceId);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingInstanceId) return;

    try {
      await del(`/internal/flow-instances/${deletingInstanceId}`);
      onMutate();
      // QL-275 — deletion cascades to the instance's flow_instance_event rows,
      // so its Activity tab entries disappear.
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, "activityEvents"] });
    } catch (err: any) {
      showAlert(t.error, err?.message || t.failedToDeleteInstance, "destructive");
    }
    setDeletingInstanceId(null);
  };

  const getExternalStatusBadge = (instance: FlowInstance) => {
    if (!instance.externalStatus) return <span className="text-muted-foreground">-</span>;

    const isExecuted = instance.externalStatus === "executed";
    const badge = (
      <StatusBadge status={instance.externalStatus}>
        {isExecuted ? t.executed : t.canceled}
      </StatusBadge>
    );

    if (instance.externalStatusInfo) {
      return (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{badge}</TooltipTrigger>
            <TooltipContent>{instance.externalStatusInfo}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return badge;
  };

  const getSignedStatusBadge = (instance: FlowInstance) => {
    if (instance.signatureOrders.length === 0) {
      return <span className="text-muted-foreground">-</span>;
    }

    const latestOrder = instance.signatureOrders[0];

    const statusLabels: Record<string, string> = {
      signed: t.signedStatus,
      pending: t.waiting,
      signing: t.waiting,
      cancelled: t.cancelled,
      expired: t.expired,
      failed: t.failed,
    };

    // Plain badge: the row link is the row's only link (DESIGN.md › Tables).
    return (
      <Badge variant={latestOrder.status === "signed" ? "success" : "secondary"}>
        {statusLabels[latestOrder.status] || latestOrder.status}
      </Badge>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {startableFlows.length > 0 && (
          <div className="flex justify-end">
            <Button onClick={() => setDialogOpen(true)}>{t.startFlow}</Button>
          </div>
        )}
        {instances.length === 0 ? (
          <EmptyState
            action={
              startableFlows.length > 0 ? (
                <Button onClick={() => setDialogOpen(true)}>{t.startFirstFlow}</Button>
              ) : (
                <p className="text-sm text-muted-foreground">{t.noActiveFlows}</p>
              )
            }
          >
            {t.noFlowInstances}
          </EmptyState>
        ) : (
          <Table variant="navigation">
            <TableHeader>
              <TableRow>
                <TableHead>{t.flow}</TableHead>
                <TableHead>{t.status}</TableHead>
                <TableHead>{t.externalStatus}</TableHead>
                <TableHead>{t.signed}</TableHead>
                <TableHead>{t.lastUpdated}</TableHead>
                <TableHead>{t.completed}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {instances.map((instance) => (
                <TableRow key={instance.id}>
                  {/* QL-274 — post-submission instances open on the process
                      page. Every link site decides this from the instance's
                      current status (the Activity tab and dashboard compute the
                      same target from the feed's flowStatus); a direct URL to
                      the wizard stays valid and renders read-only. */}
                  <TableCellLink
                    className="font-medium"
                    to={
                      isFlowInstanceEditable(instance.status)
                        ? `/clients/${clientId}/flows/${instance.id}`
                        : `/clients/${clientId}/flows/${instance.id}/process`
                    }
                  >
                    {instance.name || instance.flow.name}
                    {instance.name && (
                      <span className="text-muted-foreground font-normal ml-1">
                        ({instance.flow.name})
                      </span>
                    )}
                  </TableCellLink>
                  <TableCell>
                    <FlowStageBadge status={instance.status} language={userLanguage} />
                  </TableCell>
                  <TableCell>{getExternalStatusBadge(instance)}</TableCell>
                  <TableCell>{getSignedStatusBadge(instance)}</TableCell>
                  {/* The hover-date tooltips sit above the row link so they still open. */}
                  <TableCell>
                    <span className={rowControl}>
                      <TimeAgoTooltip date={parseDbTimestamp(instance.updatedAt)}>
                        {getRelativeTime(instance.updatedAt, userLanguage)}
                      </TimeAgoTooltip>
                    </span>
                  </TableCell>
                  <TableCell>
                    {instance.completedAt ? (
                      <span className={rowControl}>
                        <TimeAgoTooltip date={parseDbTimestamp(instance.completedAt)}>
                          {getRelativeTime(instance.completedAt, userLanguage)}
                        </TimeAgoTooltip>
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className={cn("text-right", rowControl)}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteClick(instance.id)}
                        tooltip={t.delete}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.startNewFlow}</DialogTitle>
            <DialogDescription className="sr-only">{t.selectFlowDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {startableFlows.map((flow) => (
              <button
                type="button"
                key={flow.id}
                className="w-full text-left p-4 border rounded hover:bg-accent cursor-pointer"
                onClick={() => handleStartFlow(flow.id)}
              >
                <div className="flex items-center gap-2 font-medium">
                  {flow.name}
                  {flow.status === "preview" && <Badge variant="secondary">{t.preview}</Badge>}
                </div>
                {flow.description && (
                  <p className="text-sm text-muted-foreground mt-1">{flow.description}</p>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteFlowInstance}
        description={t.deleteConfirmation}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
